import React, { useState, useEffect } from 'react';
import { postRequest } from './Services/HttpClient';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import RuleTraceDisplay from './RuleTraceDisplay';
import SwaraDharmaDisplay from './SwaraDharmaDisplay';
import SanskritKeyboard from './SanskritKeyboard';
import './VikrutiPaatham.css';

const STORAGE_KEY_PREFIX = 'vikruti_last_input_';
const FULLWIDTH_STORAGE_KEY = 'vikruti_fullwidth_output';

const PAATHAM_TYPE_STORAGE_KEY = 'vikruti_paatham_type';

const VikrutiPaatham = (props) => {
  // Use props.paathamType if provided, otherwise use state with localStorage
  const [selectedPaathamType, setSelectedPaathamType] = useState(() => {
    if (props.paathamType) return props.paathamType;
    const saved = localStorage.getItem(PAATHAM_TYPE_STORAGE_KEY);
    return saved || 'krama';
  });

  const paathamType = props.paathamType || selectedPaathamType;
  const storageKey = STORAGE_KEY_PREFIX + paathamType;

  const [outputData, setOutputData] = useState('');
  const [inputData, setInputData] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return { inputWord: saved || '' };
  });

  // Save selected paatham type to localStorage
  useEffect(() => {
    if (!props.paathamType) {
      localStorage.setItem(PAATHAM_TYPE_STORAGE_KEY, selectedPaathamType);
    }
  }, [selectedPaathamType, props.paathamType]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vedshaakha, setVedshaakha] = useState('तैत्तिरीय');
  const [includeRuleTrace, setIncludeRuleTrace] = useState(false);
  const [ruleTrace, setRuleTrace] = useState(null);
  const [includeSwaraDharma, setIncludeSwaraDharma] = useState(false);
  const [swaraDharmaData, setSwaraDharmaData] = useState(null);
  const [fullWidthOutput, setFullWidthOutput] = useState(() => {
    const saved = localStorage.getItem(FULLWIDTH_STORAGE_KEY);
    return saved === 'true';
  });
  const [showKeyboard, setShowKeyboard] = useState(false);
  // Input is pada-pāṭha: the server strips इति, drops daṇḍas and marks,
  // and runs sandhi to each double daṇḍa before building the vikṛti.
  const [padaPaatha, setPadaPaatha] = useState(false);
  const [padaPaathaInfo, setPadaPaathaInfo] = useState(null);
  // Recitation tradition: final न् before य/व/ह + vowel (VKG, 2026-10-05)
  const [tradition, setTradition] = useState('andhra');

  // Save input to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(storageKey, inputData.inputWord || '');
  }, [inputData.inputWord, storageKey]);

  // Save fullWidthOutput preference to localStorage
  useEffect(() => {
    localStorage.setItem(FULLWIDTH_STORAGE_KEY, fullWidthOutput.toString());
  }, [fullWidthOutput]);

  const handleChange = async () => {
    if (!inputData.inputWord || !inputData.inputWord.trim()) return;

    setOutputData('');
    setRuleTrace(null);
    setPadaPaathaInfo(null);
    setSwaraDharmaData(null);
    setError(null);
    setLoading(true);

    try {
      const requestBody = {
        input: inputData.inputWord,
        type: paathamType,
        vedshaakha: vedshaakha,
        tradition: tradition
      };
      if (padaPaatha) {
        requestBody.padaPaatha = true;
      }
      if (includeRuleTrace) {
        requestBody.includeRuleTrace = true;
      }
      if (includeSwaraDharma) {
        requestBody.includeSwaraDharma = true;
      }
      const endpoint = 'v2/vikrutipaatham';
      const result = await postRequest(endpoint, JSON.stringify(requestBody));
      if (result && result.data) {
        if (typeof result.data === 'string') {
          setOutputData(result.data);
        } else if (Array.isArray(result.data)) {
          setOutputData(result.data.join('\n'));
        } else {
          setOutputData(String(result.data));
        }
      } else if (result && result.output) {
        if (typeof result.output === 'string') {
          setOutputData(result.output);
        } else if (Array.isArray(result.output)) {
          setOutputData(result.output.join('\n'));
        } else {
          setOutputData(String(result.output));
        }
      } else if (result && result.error) {
        // Server explains plainly, e.g. a śākhā whose rules are not built yet
        setError(result.error);
      } else {
        setError('अप्रत्याशित-प्रतिक्रिया प्राप्ता। (Unexpected response format)');
      }
      if (result && result.ruleTrace) {
        setRuleTrace(result.ruleTrace);
      }
      if (result && result.swaraDharma) {
        setSwaraDharmaData(result.swaraDharma);
      }
      setPadaPaathaInfo((result && result.padaPaatha) || null);
    } catch (err) {
      console.error('VikrutiPaatham API error:', err);
      setError('विश्लेषणे त्रुटिः। कृपया पुनः प्रयासं कुर्वन्तु। (Analysis failed. Please try again.)');
    } finally {
      setLoading(false);
    }
  };

  const updateInputStateInputWord = (inputWordEvent) => {
    const inputWord = inputWordEvent.target.value;
    setInputData({ ...inputData, inputWord });
  };

  const clear = () => {
    setInputData({ inputWord: '' });
    setOutputData('');
    setRuleTrace(null);
    setPadaPaathaInfo(null);
    setSwaraDharmaData(null);
    setError(null);
  };

  const handleKeyboardInput = (key) => {
    if (key === 'BACKSPACE') {
      setInputData(prev => ({
        ...prev,
        inputWord: prev.inputWord.slice(0, -1)
      }));
    } else {
      setInputData(prev => ({
        ...prev,
        inputWord: (prev.inputWord || '') + key
      }));
    }
  };

  const paathamTypeLabels = {
    krama: 'क्रम-पाठः (Krama)',
    jata: 'जटा-पाठः (Jaṭā)',
    jataa: 'जटा-पाठः (Jaṭā)',
    maala: 'माला-पाठः (Mālā)',
    shikha: 'शिखा-पाठः (Śikhā)',
    rekha: 'रेखा-पाठः (Rekhā)',
    dhvaja: 'ध्वज-पाठः (Dhvaja)',
    danda: 'दण्ड-पाठः (Daṇḍa)',
    ratha: 'रथ-पाठः (Ratha)',
    ghana: 'घन-पाठः (Ghana)'
  };

  const paathamTypeDescriptions = {
    krama: 'पदानां क्रमेण पाठः - Sequential word pairs (foundation)',
    jata: 'जटामुखं विकृतिपाठः - Braid pattern (ABBAAB)',
    jataa: 'जटामुखं विकृतिपाठः - Braid pattern (ABBAAB)',
    maala: 'मालामुखं विकृतिपाठः - Garland pattern (ABA)',
    shikha: 'शिखामुखं विकृतिपाठः - Peak pattern (Jata + next word)',
    rekha: 'रेखामुखं विकृतिपाठः - Line pattern (progressive groups)',
    dhvaja: 'ध्वजमुखं विकृतिपाठः - Flag pattern (ping-pong)',
    danda: 'दण्डमुखं विकृतिपाठः - Staff pattern (accumulating)',
    ratha: 'रथमुखं विकृतिपाठः - Chariot pattern (complex)',
    ghana: 'घनमुखं विकृतिपाठः - Dense pattern (most complex)'
  };

  return (
    <Container fluid="md" className="vikruti-page">
      {/* Header with title - only show if using separate routes */}
      {props.paathamType && (
        <Row>
          <div className="vikruti-header">
            <h1 className="vikruti-title">
              {paathamTypeLabels[paathamType] || paathamType}
            </h1>
            <p className="vikruti-description">
              {paathamTypeDescriptions[paathamType]}
            </p>
          </div>
        </Row>
      )}

      {/* Unified header with paatham type selection */}
      {!props.paathamType && (
        <Row>
          <div className="vikruti-header">
            <h1 className="vikruti-title">विकृतिपाठः (Vikruti)</h1>
            <p className="vikruti-description">वेदपाठप्रकाराः</p>
          </div>
        </Row>
      )}

      {/* Options Bar */}
      <Row className="vikruti-options-row">
        <div className="vikruti-options">
          {/* Paatham Type Dropdown - only show for unified route */}
          {!props.paathamType && (
            <div className="vikruti-paatham-type-group">
              <label className="vikruti-label">
                <span className="label-text">पाठप्रकारः:</span>
                <select
                  value={selectedPaathamType}
                  onChange={(e) => setSelectedPaathamType(e.target.value)}
                  className="vikruti-select vikruti-paatham-select"
                >
                  <option value="krama">क्रम-पाठः (Krama)</option>
                  <option value="jata">जटा-पाठः (Jaṭā)</option>
                  <option value="maala">माला-पाठः (Mālā)</option>
                  <option value="shikha">शिखा-पाठः (Śikhā)</option>
                  <option value="rekha">रेखा-पाठः (Rekhā)</option>
                  <option value="dhvaja">ध्वज-पाठः (Dhvaja)</option>
                  <option value="danda">दण्ड-पाठः (Daṇḍa)</option>
                  <option value="ratha">रथ-पाठः (Ratha)</option>
                  <option value="ghana">घन-पाठः (Ghana)</option>
                </select>
              </label>
            </div>
          )}
          <div className="vikruti-shakha-group">
            <label className="vikruti-label">
              <span className="label-text">वेदशाखा:</span>
              <select
                value={vedshaakha}
                onChange={(e) => setVedshaakha(e.target.value)}
                className="vikruti-select"
              >
                <option value="तैत्तिरीय">तैत्तिरीय</option>
                <option value="शाकल">शाकल</option>
                <option value="शौनक">शौनक</option>
                <option value="रानायनीय">रानायनीय</option>
                <option value="कौथुम">कौथुम</option>
                <option value="माध्यन्दिन">माध्यन्दिन</option>
              </select>
            </label>
          </div>
          <div className="vikruti-shakha-group">
            <label
              className="vikruti-label"
              title="आन्ध्रसम्प्रदायः: न् does not join य, व, ह + vowel. द्राविडसम्प्रदायः: न् after a short vowel joins य."
            >
              <span className="label-text">सम्प्रदायः:</span>
              <select
                value={tradition}
                onChange={(e) => setTradition(e.target.value)}
                className="vikruti-select"
              >
                <option value="andhra">आन्ध्रः</option>
                <option value="dravida">द्राविडः</option>
              </select>
            </label>
          </div>
          <div className="vikruti-checkboxes">
            <label
              className="vikruti-checkbox"
              title="Input is pada-pāṭha: इति is stripped, daṇḍas and marks removed, sandhi runs to each double daṇḍa"
            >
              <input
                type="checkbox"
                checked={padaPaatha}
                onChange={(e) => setPadaPaatha(e.target.checked)}
              />
              <span>पदपाठः</span>
            </label>
            <label className="vikruti-checkbox">
              <input
                type="checkbox"
                checked={includeRuleTrace}
                onChange={(e) => setIncludeRuleTrace(e.target.checked)}
              />
              <span>नियम-दर्शनम्</span>
            </label>
            <label className="vikruti-checkbox">
              <input
                type="checkbox"
                checked={includeSwaraDharma}
                onChange={(e) => setIncludeSwaraDharma(e.target.checked)}
              />
              <span>स्वरधर्मः</span>
            </label>
          </div>
        </div>
      </Row>

      {/* Description Panel for Selected Vikruti Type */}
      {!props.paathamType && (
        <Row className="vikruti-description-row">
          <div className="vikruti-description-panel">
            <span className="vikruti-description-icon">ℹ️</span>
            <span className="vikruti-description-text">
              {paathamTypeDescriptions[paathamType]}
            </span>
          </div>
        </Row>
      )}

      {/* Full Width Mode: Input bar and buttons above output */}
      {fullWidthOutput && (
        <Row className="vikruti-fullwidth-input-row">
          <Col>
            <div className="vikruti-fullwidth-input-bar">
              <div className="vikruti-fullwidth-input-group">
                <span className="fullwidth-label">निवेशः:</span>
                <textarea
                  className="vikruti-fullwidth-input-field"
                  rows={6}
                  value={inputData.inputWord || ''}
                  disabled={loading}
                  onChange={updateInputStateInputWord}
                  placeholder="संहिता-पाठं लिखतु...&#10;(Enter Samhita text with words separated by spaces)"
                />
              </div>
              <div className="vikruti-fullwidth-actions">
                <Button
                  variant="primary"
                  onClick={handleChange}
                  disabled={loading || !inputData.inputWord || !inputData.inputWord.trim()}
                  className="vikruti-submit-btn-compact"
                >
                  {loading ? 'विश्लेषणम्...' : 'प्रदर्शय'}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={clear}
                  className="vikruti-clear-btn-compact"
                >
                  स्वच्छम्
                </Button>
                <button
                  className="vikruti-collapse-btn"
                  onClick={() => setFullWidthOutput(false)}
                  title="Show side-by-side layout"
                >
                  ◀ द्विभागः
                </button>
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* Main Content - Side by side on tablet+, stacked on mobile */}
      <Row className={`vikruti-main-row ${fullWidthOutput ? 'full-width-mode' : ''}`}>
        {/* Input Column - hidden when full width output is enabled */}
        {!fullWidthOutput && (
          <Col xs={12} md={6} className="vikruti-col vikruti-input-col">
            <div className="vikruti-section">
              <div className="vikruti-section-header">
                <span className="section-label">निवेशः (Input)</span>
              </div>
              <textarea
                className="vikruti-textarea vikruti-input"
                name="textarea"
                value={inputData.inputWord || ''}
                disabled={loading}
                onChange={updateInputStateInputWord}
                placeholder="संहिता-पाठं लिखतु...&#10;(Enter Samhita text with words separated by spaces)"
              />
            </div>
          </Col>
        )}

        {/* Output Column - full width when toggle is enabled */}
        <Col xs={12} md={fullWidthOutput ? 12 : 6} className="vikruti-col vikruti-output-col">
          <div className="vikruti-section">
            <div className="vikruti-section-header">
              <span className="section-label">
                {paathamTypeLabels[paathamType]}
              </span>
              {!fullWidthOutput && (
                <button
                  className="vikruti-expand-btn"
                  onClick={() => setFullWidthOutput(true)}
                  title="Expand output to full width"
                >
                  ▶ विस्तृतम्
                </button>
              )}
            </div>
            <textarea
              className={`vikruti-textarea vikruti-output ${fullWidthOutput ? 'full-width' : ''}`}
              value={loading ? 'विश्लेषणम्...' : outputData}
              readOnly
              placeholder="फलं अत्र प्रदर्श्यते...&#10;(Output will appear here)"
            />
          </div>
        </Col>
      </Row>

      {/* Action Buttons - only shown in side-by-side mode */}
      {!fullWidthOutput && (
        <Row className="vikruti-actions-row">
          <div className="vikruti-actions">
            <Button
              variant="primary"
              onClick={handleChange}
              disabled={loading || !inputData.inputWord || !inputData.inputWord.trim()}
              className="vikruti-submit-btn"
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  विश्लेषणम्...
                </>
              ) : (
                'प्रदर्शय (Show)'
              )}
            </Button>
            <Button
              variant="outline-secondary"
              onClick={clear}
              className="vikruti-clear-btn"
            >
              स्वच्छं कुरु (Clear)
            </Button>
          </div>
        </Row>
      )}

      {/* Error Display */}
      {error && (
        <Row className="vikruti-error-row">
          <div className="vikruti-error">
            {error}
          </div>
        </Row>
      )}

      {/* Pada-pāṭha step: what was converted before the vikṛti */}
      {padaPaathaInfo && (
        <Row className="vikruti-error-row">
          <div className="vikruti-padapaatha-info" style={{ fontSize: '0.9em', opacity: 0.85 }}>
            पदपाठः — {padaPaathaInfo.changedPadas.length} pada(s) converted to prayoga form before sandhi
            {padaPaathaInfo.changedPadas.length > 0 && (
              <span>: {padaPaathaInfo.changedPadas.slice(0, 8).map(c => `${c.pada} → ${c.prayoga}`).join(' · ')}
                {padaPaathaInfo.changedPadas.length > 8 ? ' …' : ''}</span>
            )}
          </div>
        </Row>
      )}

      {/* Rule Trace Display */}
      {includeRuleTrace && ruleTrace && (
        <Row className="vikruti-trace-row">
          <RuleTraceDisplay ruleTrace={ruleTrace} outputText={outputData} />
        </Row>
      )}

      {/* Swara Dharma Display */}
      {includeSwaraDharma && swaraDharmaData && (
        <Row className="vikruti-swara-row">
          <SwaraDharmaDisplay
            inputSwaraDharma={swaraDharmaData.input}
            outputSwaraDharma={swaraDharmaData.output}
            outputIsNested={true}
          />
        </Row>
      )}

      {/* Sanskrit Keyboard */}
      <Row className="vikruti-keyboard-row">
        <Col>
          <SanskritKeyboard
            onKeyPress={handleKeyboardInput}
            isVisible={showKeyboard}
            onToggle={() => setShowKeyboard(!showKeyboard)}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default VikrutiPaatham;
